import { z } from 'zod';
import {
  DraftSaveBoxIdMapEntrySchema,
  DraftSaveBoxInputSchema,
  DraftSaveBoxItemIdMapEntrySchema,
  REQUEST_BOXES_PER_REQUEST_MAX,
} from '../request-boxes.js';
import { IssuedStorageObjectKeySchema, StorageObjectKeySchema } from '../storage.js';
import { RequestSchema } from './aggregate.js';
import { enforceContainerDictionaryInvariants } from './container-dictionary-refine.js';
import { RequestDestinationInputSchema } from './destinations.js';
import {
  RequestPackagingOptionIdsInputSchema,
  RequestRoadOptionIdsInputSchema,
} from './dictionary-selections.js';
import {
  REQUEST_ITEM_PACKAGE_COUNT_MAX,
  REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM,
  REQUEST_ITEM_PACKAGE_WEIGHT_KG_MAX,
  REQUEST_ITEM_PHOTOS_MAX,
} from './items.js';
import { isContainerKind, isSimpleKind, kindForbidsInsurance, RequestKindSchema } from './kind.js';
import { CarrierBidCurrencySchema } from './status.js';

// WHY: dedicated draft-aggregate save inputs/outputs for issue #521
// `POST /requests/draft` and `PUT /requests/:id/draft`. The endpoints are
// the only path that can persist exact partial DRAFT values — the create
// path remains strict (`CreateRequestInputSchema`) so non-DRAFT entry
// points cannot regress into accepting empty `name`, empty route, or
// `quantity=0`. Each item carries a `clientItemId` because the client
// owns local row identity (`draft-it-N`) before the server has minted a
// canonical `it-NNNN` — server returns `itemIdMap` so the next save can
// match by `id` and the client can re-baseline dirty state by mapped id.
//
// INVARIANT (`clientToken`): required at the controller level for
// `POST /requests/draft` (idempotency key) but optional/ignored for
// `PUT /requests/:id/draft` (id is the idempotency key, last-write-wins
// per Resolved Decision #14). Keeping it optional in the schema lets one
// shape serve both endpoints and matches the pre-#415 partial UNIQUE
// INDEX semantics — see `CreateRequestInputSchema.clientToken` for the
// full contract.
/**
 * @aiPurpose package input for the draft-save aggregate path
 *   (`POST /requests/draft`, `PUT /requests/:id/draft`). Mirrors the
 *   item draft-save contract: every numeric/dimension field is
 *   non-negative so partially typed DRAFT/RETURN rows persist.
 *   `clientPackageId` is the client-owned local identity that the
 *   server maps back to the minted UUID via `packageIdMap`.
 * @aiInvariant `id` is present iff the package row already exists on
 *   the server. Cross-item / cross-request ids are rejected by the
 *   draft service before any write, mirroring the existing item
 *   ownership behavior (Resolved Decision §invisible/not-found).
 * @aiSecurity bounded so a malicious caller cannot pad the response
 *   `packageIdMap` with megabyte strings.
 */
export const DraftSavePackageInputSchema = z
  .object({
    clientPackageId: z.string().min(1).max(64),
    id: z.string().uuid().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    widthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    depthCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    heightCm: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_DIMENSION_MAX_CM),
    weightKg: z.number().nonnegative().max(REQUEST_ITEM_PACKAGE_WEIGHT_KG_MAX),
    count: z.number().int().nonnegative().max(REQUEST_ITEM_PACKAGE_COUNT_MAX),
  })
  .strict();
export type DraftSavePackageInput = z.infer<typeof DraftSavePackageInputSchema>;

const DraftSaveItemInputSchema = z
  .object({
    // WHY: `clientItemId` is the local row identity the client owns
    // until the server mints `id`. Bounded so a malicious caller cannot
    // pad the response `itemIdMap` with megabyte strings; the editor
    // generates short tokens like `draft-it-3`.
    clientItemId: z.string().min(1).max(64),
    // INVARIANT: `id` is present iff this item already exists on the
    // server — the client echoes it so the aggregate save can update by
    // server id. New items omit `id` and the server creates them via
    // `request_item_seq`. A client-supplied id that does not match an
    // existing `RequestItem` row of this DRAFT must be rejected by the
    // service to prevent row hijacking across DRAFTs.
    id: z
      .string()
      .regex(/^it-\d{4}$/, 'must match it-NNNN')
      .optional(),
    name: z.string(),
    brand: z.string(),
    material: z.string().optional(),
    description: z.string(),
    // WHY (#701): nullable mirrors `DraftRequestItemSchema` — CONTAINER-
    //   kind drafts persist null weight/volume per item. STANDARD-kind
    //   strictness for active rows is asserted at the send-readiness
    //   gate, not on draft-save.
    weight: z.number().nonnegative().nullable(),
    volume: z.number().nonnegative().nullable(),
    quantity: z.number().int().min(0).max(999999),
    // WHY (#1050): product unit count, persisted distinct from
    //   `quantity` (logistics/places mirror). Optional on draft-save —
    //   a manager may not know the SKU count yet. `null` means «explicit
    //   clear» when the client owns the field; `undefined` means «do
    //   not touch» so stale bundles round-trip unchanged.
    productQuantity: z.number().int().min(1).max(999999).nullable().optional(),
    photos: z.array(StorageObjectKeySchema).max(REQUEST_ITEM_PHOTOS_MAX),
    // WHY (#590): draft-save allows partially typed package rows for
    //   DRAFT/RETURN. Send-readiness enforces the strict bound when the
    //   transition fires. `dimensionsMode` is optional so legacy/manual
    //   draft saves keep their existing shape.
    dimensionsMode: z.boolean().optional(),
    packages: z.array(DraftSavePackageInputSchema).max(500).optional(),
  })
  .strict();
export type DraftSaveItemInput = z.infer<typeof DraftSaveItemInputSchema>;

const DraftSaveInputBaseShape = z
  .object({
    name: z.string(),
    // WHY (#700 / #1053): canonical source for arrival cities. STANDARD
    //   drafts must have at least one destination (enforced by the
    //   kind-discriminated refine below — the UI pre-fills with the
    //   default `ArrivalCity` so even a fresh STANDARD draft never carries
    //   an empty list). CONTAINER drafts must NOT carry destinations (the
    //   carriers are request-wide and the search target is
    //   `deliveryAddress`). Default `[]` lets a CONTAINER caller omit the
    //   key entirely.
    destinations: z.array(RequestDestinationInputSchema).default([]),
    // WHY (#1447): `items` is tristate on draft-save. Omitted (undefined)
    //   = PRESERVE the persisted items so a redesigned STANDARD save that
    //   no longer owns positions does not wipe legacy positions; an
    //   explicit array is authoritative (full reconcile by `id`) only for
    //   editors that own items (SIMPLE, and legacy item-based STANDARD).
    //   STANDARD no longer REQUIRES items — its totals live on the
    //   request-level `weight`/`volume` columns below. SIMPLE still owns
    //   and requires items (send-readiness gate, #1305).
    items: z.array(DraftSaveItemInputSchema).optional(),
    // WHY (#599): tristate ownership of boxes on the draft-save body.
    //   `boxes` omitted = no-op (preserve existing boxes; lets a stale
    //   client save without wiping boxes it does not understand);
    //   `boxes: []` = client owns boxes and intends to delete all;
    //   `boxes: [...]` = client owns boxes and this array is
    //   authoritative (full reconcile by `id`). Optional in the schema
    //   so the existing item-only draft-save bundles round-trip
    //   unchanged through this same shape.
    boxes: z.array(DraftSaveBoxInputSchema).max(REQUEST_BOXES_PER_REQUEST_MAX).optional(),
    // WHY (#701): same defaults as `CreateRequestInputSchema` — a
    //   pre-#701 client omits `kind` and produces a STANDARD draft.
    kind: RequestKindSchema.default('STANDARD'),
    transportTypeId: z.string().uuid().nullable().default(null),
    // WHY (#996): request-level Roads/Packaging multiselects on the
    //   draft-save body. OPTIONAL — `undefined` (omitted key) preserves
    //   the persisted selection so a legacy client that does not know
    //   about these fields cannot accidentally wipe them on save. An
    //   explicit empty array `[]` is the authoritative "no selections"
    //   wipe signal. Service-layer validation rejects duplicates and
    //   inactive ids; the update path tolerates inactive ids that were
    //   already part of the prior selection.
    roadOptionIds: RequestRoadOptionIdsInputSchema.optional(),
    packagingOptionIds: RequestPackagingOptionIdsInputSchema.optional(),
    // WHY (#1052): China-only insurance fields on the draft-save body.
    //   Tristate semantics matching the surrounding draft-save contract:
    //   omitted (undefined) = preserve persisted value (legacy bundles
    //   round-trip unchanged); present (with a value) = authoritative
    //   replacement. The cross-field invariant is applied below only
    //   when `insuranceRequested` is supplied — the service merges
    //   against the persisted row for partial updates.
    insuranceRequested: z.boolean().optional(),
    insuranceCargoValue: z.number().positive().nullable().optional(),
    insuranceCargoCurrency: CarrierBidCurrencySchema.nullable().optional(),
    // WHY (#1447, #2116): STANDARD/CONTAINER request-level total weight (kg) +
    //   volume (m³) on the draft-save body. Tristate semantics matching the
    //   surrounding draft-save contract: omitted (undefined) preserves the
    //   persisted value (legacy item-based bundles round-trip unchanged),
    //   present is authoritative. The service persists them for STANDARD and
    //   CONTAINER; SIMPLE keeps the item/box-derived aggregate.
    weight: z.number().nonnegative().nullable().optional(),
    volume: z.number().nonnegative().nullable().optional(),
    // WHY (#1053): container delivery address on draft-save. Tristate
    //   semantics — omitted preserves the persisted value (legacy
    //   bundles round-trip), present is authoritative. The service
    //   merges with the persisted row and applies the kind-aware
    //   invariant there; the wire enforces only the structural
    //   STANDARD-side rejection of a non-null value.
    deliveryAddress: z
      .string()
      .max(2000)
      .nullable()
      .optional()
      .transform((value) =>
        value === undefined ? undefined : value === null ? null : value.trim(),
      ),
    // WHY (#1392, #2584): SIMPLE/CONTAINER pickup address on draft-save. Tristate
    //   semantics — omitted preserves the persisted value (legacy
    //   bundles round-trip), present is authoritative. The service
    //   merges with the persisted row and applies the kind-aware
    //   invariant there; the wire enforces only the structural
    //   STANDARD-side rejection of a non-null value. Blank input becomes
    //   null. Never mirrored into `routeTo`.
    pickupAddress: z
      .string()
      .max(2000)
      .nullable()
      .optional()
      .transform((value) => {
        if (value === undefined || value === null) return value;
        const trimmed = value.trim();
        return trimmed.length === 0 ? null : trimmed;
      }),
    clientToken: z
      .string()
      .uuid('clientToken must be a valid RFC 4122 UUID')
      .transform((s) => s.toLowerCase())
      .optional(),
    // WHY (#1596): explicitly-scoped funnel link on the draft-save body.
    //   ONLY `POST /requests/draft` (create) may bind a Lead/Deal at draft
    //   time — the create path gates target visibility server-side via
    //   `assertLeadVisibleForLink` / `assertDealVisibleForLink` and emits
    //   `LEAD_/DEAL_REQUEST_LINKED` activity. `PUT /requests/:id/draft`
    //   (update) NEVER mutates a link: the same shared shape serves both
    //   endpoints, so the update service rejects a present link field
    //   instead. Optional + nullable so legacy bundles that omit the key
    //   round-trip unchanged and an explicit `null` is a valid "no link".
    leadId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
  })
  .strict();

export const DraftSaveInputSchema = DraftSaveInputBaseShape.superRefine((body, ctx) => {
  // WHY (#701): same cross-field invariants as create — CONTAINER
  //   requires `transportTypeId` and forbids any non-empty `boxes` on
  //   the same payload. STANDARD must carry null `transportTypeId`. We
  //   do NOT enforce per-item weight/volume strictness here because the
  //   draft-save path is the partial-data persistence surface; the
  //   active strictness lives at send-readiness.
  if (isContainerKind(body.kind)) {
    if (body.transportTypeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request requires transportTypeId',
        path: ['transportTypeId'],
      });
    }
    if (body.boxes !== undefined && body.boxes.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request forbids boxes',
        path: ['boxes'],
      });
    }
    // WHY (#1053): destinations are STANDARD-only — CONTAINER carriers
    //   are request-wide and the row keyed on `deliveryAddress`. The
    //   service-side draft-update path additionally skips the
    //   reconcile, so an explicit empty array is the only legal value.
    if (body.destinations.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request must not carry destinations',
        path: ['destinations'],
      });
    }
    // WHY (#1446): the redesigned CONTAINER form dropped the inline
    //   Roads/Packaging selectors, so a CONTAINER draft-save rejects a
    //   non-empty selection. Omitted keys preserve hidden legacy
    //   selections (tristate `.optional()` above); an empty array is
    //   also accepted — only a non-empty selection is rejected.
    enforceContainerDictionaryInvariants(body, ctx);
  } else {
    if (body.transportTypeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null transportTypeId',
        path: ['transportTypeId'],
      });
    }
    // WHY (#1053): STANDARD on draft-save rejects a non-null
    //   deliveryAddress at the wire — a flipped flag with stale text
    //   would otherwise paper over to the service. CONTAINER side
    //   relaxes (partial drafts may omit the address until typing
    //   finishes — send-readiness reasserts non-empty).
    if (body.deliveryAddress !== undefined && body.deliveryAddress !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null deliveryAddress',
        path: ['deliveryAddress'],
      });
    }
    // WHY (#1392, #2584): STANDARD rejects a non-null pickupAddress at
    //   the wire. SIMPLE owns an optional origin address; CONTAINER remains
    //   relaxed while a partial draft is being typed.
    if (
      !isSimpleKind(body.kind) &&
      body.pickupAddress !== undefined &&
      body.pickupAddress !== null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null pickupAddress',
        path: ['pickupAddress'],
      });
    }
    // WHY (#1053 / #700): STANDARD drafts require at least one
    //   destination so the search-hash mirror always has a city name.
    //   The UI pre-fills the default ArrivalCity, so even a brand-new
    //   draft never legitimately reaches this path with an empty array.
    if (body.destinations.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        message: 'standard request requires at least one destination',
        path: ['destinations'],
      });
    }
    // WHY (#1303 / #1453): SIMPLE and STANDARD flowed through every
    //   non-container check above but additionally forbid requesting
    //   insurance — SIMPLE per #1303, STANDARD added by #1453 after its
    //   insurance UI was removed (#1448). Reject the flag itself here for
    //   both; CONTAINER is excluded (it still permits insurance).
    if (kindForbidsInsurance(body.kind) && body.insuranceRequested === true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // WHY (#1303): keep the exact SIMPLE wording so SIMPLE consumers and
        //   tests are unchanged; STANDARD gets its own kind-appropriate copy.
        message: isSimpleKind(body.kind)
          ? 'simple request forbids insurance'
          : 'standard request forbids insurance',
        path: ['insuranceRequested'],
      });
    }
    // WHY (#1303): the cargo-value / cargo-currency / boxes "must be null /
    //   empty" structural floor stays SIMPLE-only — a STANDARD draft may
    //   legitimately carry those columns (and boxes), so this block must NOT
    //   widen to STANDARD.
    if (isSimpleKind(body.kind)) {
      if (body.insuranceCargoValue !== undefined && body.insuranceCargoValue !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'simple request must have null insuranceCargoValue',
          path: ['insuranceCargoValue'],
        });
      }
      if (body.insuranceCargoCurrency !== undefined && body.insuranceCargoCurrency !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'simple request must have null insuranceCargoCurrency',
          path: ['insuranceCargoCurrency'],
        });
      }
      if (body.boxes !== undefined && body.boxes.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'simple request forbids boxes',
          path: ['boxes'],
        });
      }
    }
  }
  // WHY (#1052): on draft-save, applying the both-or-neither rule
  //   strictly would block legitimate intermediate states (manager
  //   toggles the flag before typing the value). We enforce only the
  //   structural "false → both null" half: if the body explicitly
  //   declares `insuranceRequested = false` AND ships non-null
  //   value/currency in the same body, that is the contract violation
  //   the service-layer would otherwise paper over. The
  //   "requested:true requires value/currency" half stays a
  //   send-readiness gate, mirroring how DRAFT relaxes other strictness.
  if (body.insuranceRequested === false) {
    if (body.insuranceCargoValue !== undefined && body.insuranceCargoValue !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'insuranceCargoValue must be null when insuranceRequested is false',
        path: ['insuranceCargoValue'],
      });
    }
    if (body.insuranceCargoCurrency !== undefined && body.insuranceCargoCurrency !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'insuranceCargoCurrency must be null when insuranceRequested is false',
        path: ['insuranceCargoCurrency'],
      });
    }
  }
});
export type DraftSaveInput = z.infer<typeof DraftSaveInputSchema>;

// WHY: server returns the saved DRAFT row, the canonical-id map for
// every item the client sent (existing matched by `id`, new minted), and
// the cleanup attempt summary (which keys it tried to delete after the
// commit landed and which of those failed). Cleanup is non-blocking per
// Resolved Decision #17 — a failed key here is a UI warning, not a
// transaction-rollback signal. The `request` field rounds through the
// status-aware `RequestSchema`, so a partial DRAFT (empty name, empty
// route, `quantity=0` items) parses cleanly here while non-DRAFT rows on
// list/detail surfaces remain strict.
const DraftSaveItemIdMapEntrySchema = z
  .object({
    clientItemId: z.string().min(1),
    id: z.string().regex(/^it-\d{4}$/, 'must match it-NNNN'),
  })
  .strict();
export type DraftSaveItemIdMapEntry = z.infer<typeof DraftSaveItemIdMapEntrySchema>;

/**
 * @aiPurpose canonical-id map for the package rows the client sent
 *   alongside the draft-save body. The server emits one entry per
 *   package row regardless of whether the row was new or pre-existing
 *   so the client can re-baseline dirty state by mapped id.
 * @aiInvariant `clientItemId` references an item the client also sent;
 *   `clientPackageId` is unique within that item; `id` is the server
 *   UUID for the persisted package row.
 */
const DraftSavePackageIdMapEntrySchema = z
  .object({
    clientItemId: z.string().min(1),
    clientPackageId: z.string().min(1),
    id: z.string().uuid(),
  })
  .strict();
export type DraftSavePackageIdMapEntry = z.infer<typeof DraftSavePackageIdMapEntrySchema>;

const DraftPhotoCleanupSummarySchema = z
  .object({
    attempted: z.array(StorageObjectKeySchema),
    failed: z.array(StorageObjectKeySchema),
  })
  .strict();
export type DraftPhotoCleanupSummary = z.infer<typeof DraftPhotoCleanupSummarySchema>;

export const DraftSaveResponseSchema = z
  .object({
    request: RequestSchema,
    itemIdMap: z.array(DraftSaveItemIdMapEntrySchema),
    // WHY (#590): rolling-deploy tolerance — optional so a stale-bundle
    //   client built before this expand can still parse a backend
    //   response that includes the new key, AND so an old backend
    //   without #590 keeps round-tripping the new schema with the key
    //   simply absent.
    packageIdMap: z.array(DraftSavePackageIdMapEntrySchema).optional(),
    // WHY (#599): rolling-deploy tolerance for boxes id maps — emitted
    //   only when the draft-save body owned boxes (Decision #29). A
    //   legacy body that omits `boxes` receives an old-safe response
    //   without these keys.
    boxIdMap: z.array(DraftSaveBoxIdMapEntrySchema).optional(),
    boxItemIdMap: z.array(DraftSaveBoxItemIdMapEntrySchema).optional(),
    cleanup: DraftPhotoCleanupSummarySchema,
  })
  .strict();
export type DraftSaveResponse = z.infer<typeof DraftSaveResponseSchema>;

// WHY: explicit cleanup endpoints for the close-without-save path so the
// client never holds a delete-by-key capability. Two endpoints because
// the auth contract differs: `POST /requests/:id/draft/photo-cleanup`
// proves ownership against the persisted DRAFT row, while
// `POST /requests/draft/photo-cleanup` runs without a request id and
// authorizes purely on the actor's namespace + the requirement that no
// `RequestItem` references the key. Both endpoints accept a bounded
// batch of object keys to keep the storage-driver surface narrow and
// auditable.
// INVARIANT: cleanup endpoints parse `<scope>/<userId>/<uuid>` to
// authorize the delete. Enforce `IssuedStorageObjectKeySchema` at the
// wire so a client cannot smuggle a forward-compat or whitespace-padded
// key past the authz check by sending a value that the loose
// `StorageObjectKeySchema` would have admitted.
export const DraftPhotoCleanupRequestSchema = z
  .object({
    objectKeys: z.array(IssuedStorageObjectKeySchema).min(1).max(50),
  })
  .strict();
export type DraftPhotoCleanupRequest = z.infer<typeof DraftPhotoCleanupRequestSchema>;

export const DraftPhotoCleanupResponseSchema = DraftPhotoCleanupSummarySchema;
export type DraftPhotoCleanupResponse = z.infer<typeof DraftPhotoCleanupResponseSchema>;
